import {
  Box,
  HStack,
  IconButton,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { AffiliatesTable } from "./AffiliatesTable";
import {
  EventDetailAffiliatesSection,
  KYDAffiliate,
  UpdateAffiliateRequest,
} from "@common/interfaces/KYDEvent";
import { SectionProps } from "../sections/sectionsComponents";
import { EditModalType } from "../../../utils";
import EditAffiliatesModal from "./EditAffiliatesModal";
import { useCallback, useState } from "react";
import { useDialog } from "../../../Common/Dialog";
import { useLoading } from "../../hooks/form";
import actions from "../../../actions";
import AddAffiliateModal from "./AddAffiliateModal";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import EventDetailTitle from "../../EventDetailTitle";

const AffiliatesSection = ({
  data = {
    affiliates: [],
    affiliates_report_link: "",
    currency: undefined,
    id: undefined,
    link: undefined,
    short_link: undefined,
    status: undefined,
    timezone: undefined,
  },
  permissions,
  isLoading,
  modalType,
  refetchEvent,
  setModalType,
}: SectionProps<EventDetailAffiliatesSection["data"]>) => {
  const [editingAffiliate, setEditingAffiliate] = useState<KYDAffiliate | null>(
    null
  );
  const [deleteAffiliate, setDeleteAffiliate] = useState<KYDAffiliate | null>(
    null
  );

  const toast = useToast();
  const dialog = useDialog();
  const [loading, updateLoading] = useLoading({
    edit_affiliate: false,
    create_affiliates: false,
  });

  const onEditAffiliate = useCallback(
    async (affiliate: KYDAffiliate, mode: "edit" | "delete") => {
      let _;
      updateLoading("edit_affiliate", true);
      try {
        const request: UpdateAffiliateRequest = {
          name: mode === "edit" ? affiliate.name : undefined,
          status: mode === "delete" ? "DELETED" : undefined,
        };

        _ = await actions.ADMIN.updateAffiliate(data.id, affiliate.id, request);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        setEditingAffiliate(null);
        setDeleteAffiliate(null);

        if (mode === "edit") {
          toast({
            title: "Affiliate Updated",
            description: "Affiliate has been updated",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else if (mode === "delete") {
          toast({
            title: "Affiliate Deleted",
            description: "Affiliate has been deleted",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("edit_affiliate", false);
      return _;
    },
    [updateLoading, refetchEvent, dialog]
  );

  const onCreateAffiliates = useCallback(
    async ({ quantity }: { quantity: number }) => {
      let _;
      updateLoading("create_affiliates", true);
      try {
        if (!data.id) {
          throw Error("NO event ID provided");
        }

        _ = await actions.ADMIN.createAffiliates(data.id, {
          quantity: quantity.toString(),
        });
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        toast({
          title: `${quantity} Affiliates Created`,
          description: "Affiliates have been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("create_affiliates", false);
      return _;
    },
    [updateLoading, refetchEvent]
  );

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  if (!data) {
    return null;
  }

  return (
    <Stack>
      <AddAffiliateModal
        isOpen={modalType === EditModalType.CREATE_AFFILIATES}
        data={data}
        isLoading={isLoading || loading.create_affiliates}
        onClose={() => {
          setModalType(EditModalType.INACTIVE);
        }}
        onCreateAffiliates={onCreateAffiliates}
      />
      <EditAffiliatesModal
        isOpen={!!editingAffiliate}
        mode="edit"
        affiliate={editingAffiliate}
        loading={loading}
        onClose={() => {
          setModalType(EditModalType.INACTIVE);
          setEditingAffiliate(null);
          setDeleteAffiliate(null);
        }}
        onEditAffiliate={onEditAffiliate}
      />
      <EditAffiliatesModal
        isOpen={!!deleteAffiliate}
        mode="delete"
        affiliate={deleteAffiliate}
        loading={loading}
        onClose={() => {
          setModalType(EditModalType.INACTIVE);
          setEditingAffiliate(null);
          setDeleteAffiliate(null);
        }}
        onEditAffiliate={onEditAffiliate}
      />
      <Stack direction={"row"} justify={"space-between"} w="100%">
        <HStack justify={"space-between"} w="100%">
          <EventDetailTitle>Affiliates</EventDetailTitle>

          <ShowIfHasPermission permissions={permissions} checkFor="write">
            <IconButton
              isDisabled={isLoading}
              variant={"outline"}
              aria-label="Add affiliates"
              icon={<FaPlus />}
              onClick={() => setModalType(EditModalType.CREATE_AFFILIATES)}
            />
          </ShowIfHasPermission>
        </HStack>
      </Stack>
      <Text color="gray.500" fontSize={"sm"}>
        Affiliate data is updated once per day automatically
      </Text>
      <Box
        py={4}
        overflowX={data.affiliates.length === 0 ? "inherit" : "scroll"}
      >
        {data.affiliates.length === 0 ? (
          <Text color="gray.500">No affiliates created</Text>
        ) : null}
        {data.affiliates.length > 0 ? (
          <AffiliatesTable
            readOnly={!hasPermission("write")}
            onEdit={(affiliate) => {
              setEditingAffiliate(affiliate);
            }}
            onDelete={(affiliate) => {
              setDeleteAffiliate(affiliate);
            }}
            isDisabled={isLoading}
            affiliates={data.affiliates}
            loading={loading}
          />
        ) : null}
      </Box>
    </Stack>
  );
};

export default AffiliatesSection;
